import { useRef, useState } from "react";
import useToast from "~/components/ui/toast";

interface UseAsyncMutationOptions<T> {
  // Toast shown when the mutation rejects. Required so callers don't silently
  // swallow errors.
  errorMessage: string;
  // Runs after the mutation resolves. Awaited so callers can chain SWR
  // revalidations or navigation before isPending flips back to false.
  onSuccess?: (result: T) => void | Promise<void>;
  // Runs after the error toast. Use for extra UI cleanup (e.g. closing a
  // nested dialog). Throwing here is not caught.
  onError?: (error: unknown) => void;
}

interface UseAsyncMutationReturn<A extends unknown[]> {
  execute: (...args: A) => Promise<void>;
  isPending: boolean;
}

// Wraps an async mutation with a pending flag and error toast so dialogs
// don't reimplement the try/catch/finally + showError boilerplate.
export const useAsyncMutation = <T, A extends unknown[]>(
  fn: (...args: A) => Promise<T>,
  options: UseAsyncMutationOptions<T>
): UseAsyncMutationReturn<A> => {
  const { showError } = useToast();
  const [isPending, setIsPending] = useState(false);
  // Stash options in a ref so callers can pass fresh closures each render
  // without us needing to memoize execute.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const execute = async (...args: A) => {
    setIsPending(true);
    try {
      const result = await fn(...args);
      await optionsRef.current.onSuccess?.(result);
    } catch (error) {
      showError(optionsRef.current.errorMessage);
      optionsRef.current.onError?.(error);
    } finally {
      setIsPending(false);
    }
  };

  return { execute, isPending };
};
